/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
import { useState, useEffect, useRef } from 'react';
import {
  useUpdate,
  useRedirect,
  useNotify,
  useDelete,
  useGetOne,
  useRefresh,
} from 'react-admin';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Box,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  Checkbox,
  TextField,
  Alert,
  Collapse,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import type {
  Show,
  Genre,
  MixingTrack,
  Weekday,
  HostPersona,
} from '@musedrops/shared';
import { logger } from '@musedrops/shared';
import {
  validateMixingSpec,
  formatMixingSpecJson,
  type MixingValidationError,
} from '@musedrops/shared/src/mixing-utils';
import { checkSlugUniqueness } from '../../../data/show';
import { getGenPromptContent } from '../../../data/gen-prompt';
import { VoiceProfileSelector } from '../../../shared/components/VoiceProfileSelector';
import { HostPersonaSelector } from '../../../shared/components/HostPersonaSelector';
import { GenreSelector } from '../components/GenreSelector';
import { MusicUploadSection } from '../components/MusicUploadSection';
import { IntroMusicSection } from '../components/IntroMusicSection';
import { OutroMusicSection } from '../components/OutroMusicSection';
import { ThemeMusicSection } from '../components/ThemeMusicSection';
import { AudioMixTester } from '../components/AudioMixTester';
import { ShowEditHostsTab } from '../components/ShowEditHostsTab';
import { ShowEditLanguagesTab } from '../components/ShowEditLanguagesTab';
import { FormTextField } from '../../../shared/components/forms';
import { EntityMetadata } from '../../../shared/components/EntityMetadata';
import { EntityEnabledToggle } from '../../../shared/components/EntityEnabledToggle';
import { TagsInput } from '../../../shared/components/TagsInput';
import { DebugBadge } from '../../../shared/components/DebugBadge';
import { ImageGenerationSection } from '../../../shared/components/ImageGenerationSection';
import { ProtectedDeleteDialog } from '../../../shared/components/ProtectedDeleteDialog';
import { ShowActionsSection } from '../components/ShowActionsSection';
import { LAYOUT } from '../../../theme';
import { supabase } from '../../../services/supabase';
import { generateShowImage } from '../../../services/imageService';
import { generateShowThemeMusic } from '../../../services/musicService';
import { useQueueJob } from '../../../hooks/useQueueJob';
import { ENV_CONFIG, isProduction } from '../../../config/env';
import { HostPersonaApi } from '../../../services/hostPersonaApi';

type ShowRecord = Show & { genre?: Genre };

const ShowEditForm = ({ record }: { record: ShowRecord }) => {
  const [update] = useUpdate();
  const [deleteOne] = useDelete();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();

  // Use queue job hook to watch for async image generation completion
  const {
    watchJob,
    isWaiting: isGeneratingImage,
    status: imageJobStatus,
  } = useQueueJob({
    timeout: 3 * 60 * 1000, // 3 minutes for image generation
    onComplete: output => {
      const fullPath = output?.path as string;
      if (fullPath) {
        notify('Show image generated successfully', { type: 'success' });
        // Real-time subscription will update imageUpdatedAt automatically
      } else {
        notify('Image generated but path not found in output', {
          type: 'error',
        });
      }
    },
    onError: errorMsg => {
      notify(`Show image generation failed: ${errorMsg}`, { type: 'error' });
    },
    onTimeout: () => {
      notify('Image generation timed out', { type: 'error' });
    },
  });

  // Use queue job hook to watch for async theme music generation completion (Issue #601)
  const {
    watchJob: watchMusicJob,
    isWaiting: isGeneratingThemeMusic,
    status: musicJobStatus,
  } = useQueueJob({
    timeout: 5 * 60 * 1000, // 5 minutes for music generation (Kie.ai can be slow)
    onComplete: output => {
      const fullPath = output?.path as string;
      if (fullPath) {
        notify('Theme music generated successfully', { type: 'success' });
        // Real-time subscription will update themeMusicUpdatedAt automatically
      } else {
        notify('Theme music generated but path not found in output', {
          type: 'error',
        });
      }
    },
    onError: errorMsg => {
      notify(`Theme music generation failed: ${errorMsg}`, { type: 'error' });
    },
    onTimeout: () => {
      notify('Theme music generation timed out', { type: 'error' });
    },
  });

  const [topic, setTopic] = useState('');
  const [slug, setSlug] = useState<string | null>('');
  const [storyScope, setStoryScope] = useState<string | null>('');
  const [titleInstructions, setTitleInstructions] = useState<string | null>('');
  const [style, setStyle] = useState<string | null>('');
  const [audioLogoDesc, setAudioLogoDesc] = useState<string | null>('');
  const [musicPrompt, setMusicPrompt] = useState<string | null>('');
  const [imagePrompt, setImagePrompt] = useState<string | null>('');
  const [storyScriptPrompt, setStoryScriptPrompt] = useState<string | null>('');
  // Local timestamp state for media files (files use conventional names)
  const [introMusicUpdatedAt, setIntroMusicUpdatedAt] = useState<string | null>(
    null
  );
  const [outroMusicUpdatedAt, setOutroMusicUpdatedAt] = useState<string | null>(
    null
  );
  const [musicUpdatedAt, setMusicUpdatedAt] = useState<string | null>(null);
  const [imageUpdatedAt, setImageUpdatedAt] = useState<string | null>(null);
  const [themeMusicUpdatedAt, setThemeMusicUpdatedAt] = useState<string | null>(
    null
  );
  const [announceUpdatedAt, setAnnounceUpdatedAt] = useState<string | null>(
    null
  );
  const [enabled, setEnabled] = useState(true);
  const [enabledBusy, setEnabledBusy] = useState(false);
  const [showDisabledReason, setShowDisabledReason] = useState<string | null>(
    null
  );
  const [showDisabledAt, setShowDisabledAt] = useState<string | null>(null);
  const [allowStoryImages, setAllowStoryImages] = useState(true);
  const [voiceProfile1, setVoice1Profile] = useState<string>('');
  const [voiceInstructions1, setVoice1Instructions] = useState<string>('');
  const [voiceProfile2, setVoice2Profile] = useState<string>('');
  const [voiceInstructions2, setVoice2Instructions] = useState<string>('');
  const [voiceIndex1, setVoiceIndex1] = useState<number | null>(null);
  const [voiceIndex2, setVoiceIndex2] = useState<number | null>(null);
  const [hostPersona1, setHostPersona1] = useState<string | null>(null);
  const [hostPersona2, setHostPersona2] = useState<string | null>(null);
  const [scriptInstructions1, setScriptInstructions1] = useState<string>('');
  const [scriptInstructions2, setScriptInstructions2] = useState<string>('');
  const [dynamicsKey, setDynamicsKey] = useState<string | null>(null);
  const [personas, setPersonas] = useState<HostPersona[]>([]);
  const [genreId, setGenreId] = useState<string>('');
  // Track which record ID has been initialized to prevent re-initialization on object reference changes
  const initializedRecordIdRef = useRef<string | null>(null);
  const [lang, setLang] = useState<string | null>(null); // Language: null = all languages
  const [tags, setTags] = useState<string | null>('');
  const [usedDebug, setUsedDebug] = useState(false);
  const [description, setDescription] = useState<string | null>('');
  const [taxonomy, setTaxonomy] = useState<string | null>('');
  const [itunesCategory, setItunesCategory] = useState<string | null>('');
  const [itunesSubcategory, setItunesSubcategory] = useState<string | null>('');
  const [isDistributed, setIsDistributed] = useState(false);
  const [showDistributionOffDialog, setShowDistributionOffDialog] = useState(false);
  const [urlYoutube, setUrlYoutube] = useState<string | null>('');
  const [urlSpotify, setUrlSpotify] = useState<string | null>('');
  const [urlApple, setUrlApple] = useState<string | null>('');
  const [mixingSpec, setMixingSpec] = useState<string>(''); // JSON string for editing
  const [mixingSpecErrors, setMixingSpecErrors] = useState<
    MixingValidationError[]
  >([]);
  const [showMixingSpecBlockedDialog, setShowMixingSpecBlockedDialog] =
    useState(false);
  const [showScriptPromptBlockedDialog, setShowScriptPromptBlockedDialog] =
    useState(false);
  const [showMixingHelp, setShowMixingHelp] = useState(false);
  const [showProtectedDeleteDialog, setShowProtectedDeleteDialog] =
    useState(false);
  const [showStandardDeleteDialog, setShowStandardDeleteDialog] =
    useState(false);
  const [slugError, setSlugError] = useState('');
  const [urlErrors, setUrlErrors] = useState<{
    youtube?: string;
    spotify?: string;
    apple?: string;
  }>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [storyCount, setStoryCount] = useState<number>(0);

  // Scheduling configuration
  const [customPublishWeekdays, setCustomPublishWeekdays] = useState<
    string | null
  >(null); // Comma-separated weekdays
  const [selectedWeekdays, setSelectedWeekdays] = useState<Set<Weekday>>(
    new Set()
  );
  const [activeTab, setActiveTab] = useState(0);

  // Note: Timestamp fields (imageUpdatedAt, musicUpdatedAt, etc.) are intentionally
  // excluded from originalValues because they're system-managed, not user-editable.
  // Including them causes false positive "unsaved changes" warnings when background
  // processes update them via real-time subscriptions.
  const [originalValues, setOriginalValues] = useState<{
    topic: string;
    slug: string;
    storyScope: string;
    titleInstructions: string;
    style: string;
    audioLogoDesc: string;
    musicPrompt: string;
    imagePrompt: string;
    storyScriptPrompt: string;
    enabled: boolean;
    allowStoryImages: boolean;
    voiceProfile1: string;
    voiceInstructions1: string;
    voiceProfile2: string;
    voiceInstructions2: string;
    voiceIndex1: number | null;
    voiceIndex2: number | null;
    hostPersona1: string | null;
    hostPersona2: string | null;
    scriptInstructions1: string;
    scriptInstructions2: string;
    dynamicsKey: string | null;
    genreId: string;
    lang: string | null;
    tags: string;
    usedDebug: boolean;
    description: string;
    taxonomy: string;
    isDistributed: boolean;
    itunesCategory: string;
    itunesSubcategory: string;
    urlYoutube: string;
    urlSpotify: string;
    urlApple: string;
    mixingSpec: string;
    customPublishWeekdays: string | null;
  } | null>(null);

  // Fetch story count for this show
  useEffect(() => {
    if (!record?.slug) return;

    const fetchStoryCount = async () => {
      const { count, error } = await supabase
        .from('stories')
        .select('*', { count: 'exact', head: true })
        .eq('show_slug', record.slug);

      if (!error && count !== null) {
        setStoryCount(count);
      }
    };

    fetchStoryCount();
  }, [record?.slug]);

  // Load all personas on mount for persona selector
  useEffect(() => {
    const loadPersonas = async () => {
      try {
        const allPersonas = await HostPersonaApi.getAll();
        setPersonas(allPersonas);
      } catch (error) {
        logger.error('Failed to load personas:', error);
      }
    };
    loadPersonas();
  }, []);

  // Handler for Speaker 1 persona change
  // When persona selected: erase custom instructions from state
  // When Custom selected (null): preserve current custom instructions in state
  const handlePersona1Change = (personaKey: string | null) => {
    setHostPersona1(personaKey);
    if (personaKey) {
      // Persona selected: erase custom instructions from state
      setScriptInstructions1('');
      setVoice1Instructions('');
    }
  };

  // Handler for Speaker 2 persona change
  // When persona selected: erase custom instructions from state
  // When Custom selected (null): preserve current custom instructions in state
  const handlePersona2Change = (personaKey: string | null) => {
    setHostPersona2(personaKey);
    if (personaKey) {
      // Persona selected: erase custom instructions from state
      setScriptInstructions2('');
      setVoice2Instructions('');
    }
  };

  // Initialize form with record data
  // Only run when record ID changes (not on every object reference change)
  useEffect(() => {
    if (record && record.id !== initializedRecordIdRef.current) {
      // Mark this record as initialized
      initializedRecordIdRef.current = record.id;
      setTopic(record.topic);
      setSlug(record.slug);
      setStoryScope(record.storyScope || '');
      setTitleInstructions(record.titleInstructions || '');
      setStyle(record.style);
      setAudioLogoDesc(record.audioLogoDesc || '');
      setMusicPrompt(record.musicPrompt || '');
      setImagePrompt(record.imagePrompt || '');
      setStoryScriptPrompt(record.storyScriptPrompt || '');
      setIntroMusicUpdatedAt(record.introMusicUpdatedAt || null);
      setOutroMusicUpdatedAt(record.outroMusicUpdatedAt || null);
      setMusicUpdatedAt(record.musicUpdatedAt || null);
      setImageUpdatedAt(record.imageUpdatedAt || null);
      setThemeMusicUpdatedAt(record.themeMusicUpdatedAt || null);
      setAnnounceUpdatedAt(record.announceUpdatedAt || null);
      setEnabled(record.enabled);
      setShowDisabledReason(record.disabledReason ?? null);
      setShowDisabledAt(record.disabledAt ?? null);
      setAllowStoryImages(record.allowStoryImages ?? true);
      setVoice1Profile(record.voiceProfile1 || '');
      setVoice1Instructions(record.voiceInstructions1 || '');
      setVoice2Profile(record.voiceProfile2 || '');
      setVoice2Instructions(record.voiceInstructions2 || '');
      setVoiceIndex1(record.voiceIndex1 ?? null);
      setVoiceIndex2(record.voiceIndex2 ?? null);
      setHostPersona1(record.hostPersona1 ?? null);
      setHostPersona2(record.hostPersona2 ?? null);
      setScriptInstructions1(record.scriptInstructions1 || '');
      setScriptInstructions2(record.scriptInstructions2 || '');
      setDynamicsKey(record.dynamicsKey ?? null);
      setGenreId(record.genreId || '');
      setLang(record.lang ?? null); // null = all languages
      logger.info('DEBUG ShowEdit: lang loaded from record', {
        recordLang: record.lang,
        setTo: record.lang ?? null,
      });
      setTags(record.tags || '');
      setUsedDebug(record.usedDebug || false);
      setDescription(record.description || '');
      setTaxonomy(record.taxonomy || '');
      setIsDistributed(record.isDistributed || false);
      setItunesCategory(record.itunesCategory || '');
      setItunesSubcategory(record.itunesSubcategory || '');
      setUrlYoutube(record.urlYoutube || '');
      setUrlSpotify(record.urlSpotify || '');
      setUrlApple(record.urlApple || '');
      // Convert mixing spec array to JSON string for editing (with compact arrays)
      const mixingSpecStr = record.mixingSpec
        ? formatMixingSpecJson(record.mixingSpec)
        : '';
      setMixingSpec(mixingSpecStr);
      setMixingSpecErrors([]);

      // Initialize scheduling fields
      setCustomPublishWeekdays(record.customPublishWeekdays || null);

      // Parse weekdays into selectedWeekdays Set
      if (record.customPublishWeekdays) {
        const weekdaysArray = record.customPublishWeekdays
          .split(',')
          .map(d => d.trim() as Weekday);
        setSelectedWeekdays(new Set(weekdaysArray));
      } else {
        setSelectedWeekdays(new Set());
      }

      // Store original values for comparison
      setOriginalValues({
        topic: record.topic,
        slug: record.slug,
        storyScope: record.storyScope || '',
        titleInstructions: record.titleInstructions || '',
        style: record.style,
        audioLogoDesc: record.audioLogoDesc || '',
        musicPrompt: record.musicPrompt || '',
        imagePrompt: record.imagePrompt || '',
        storyScriptPrompt: record.storyScriptPrompt || '',
        enabled: record.enabled,
        allowStoryImages: record.allowStoryImages ?? true,
        voiceProfile1: record.voiceProfile1 || '',
        voiceInstructions1: record.voiceInstructions1 || '',
        voiceProfile2: record.voiceProfile2 || '',
        voiceInstructions2: record.voiceInstructions2 || '',
        voiceIndex1: record.voiceIndex1 ?? null,
        voiceIndex2: record.voiceIndex2 ?? null,
        hostPersona1: record.hostPersona1 ?? null,
        hostPersona2: record.hostPersona2 ?? null,
        scriptInstructions1: record.scriptInstructions1 || '',
        scriptInstructions2: record.scriptInstructions2 || '',
        dynamicsKey: record.dynamicsKey ?? null,
        genreId: record.genreId || '',
        lang: record.lang ?? null,
        tags: record.tags || '',
        usedDebug: record.usedDebug || false,
        description: record.description || '',
        taxonomy: record.taxonomy || '',
        isDistributed: record.isDistributed || false,
        itunesCategory: record.itunesCategory || '',
        itunesSubcategory: record.itunesSubcategory || '',
        urlYoutube: record.urlYoutube || '',
        urlSpotify: record.urlSpotify || '',
        urlApple: record.urlApple || '',
        mixingSpec: mixingSpecStr,
        customPublishWeekdays: record.customPublishWeekdays || null,
      });
    }
  }, [record]);

  // Subscribe to real-time updates for background processing changes
  useEffect(() => {
    if (!record?.id) return undefined;

    const subscription = supabase
      .channel(`show-${record.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'shows',
          filter: `id=eq.${record.id}`,
        },
        payload => {
          const newRecord = payload.new as Record<string, unknown>;
          const oldRecord = payload.old as Record<string, unknown>;

          // Only update state if the value actually changed (not just present in payload)
          if (newRecord.image_updated_at !== oldRecord.image_updated_at) {
            logger.info('Real-time update: imageUpdatedAt changed');
            const newImageUpdatedAt =
              (newRecord.image_updated_at as string) || null;
            setImageUpdatedAt(newImageUpdatedAt);
          }
          if (newRecord.music_updated_at !== oldRecord.music_updated_at) {
            logger.info('Real-time update: musicUpdatedAt changed');
            const newMusicUpdatedAt =
              (newRecord.music_updated_at as string) || null;
            setMusicUpdatedAt(newMusicUpdatedAt);
          }
          if (
            newRecord.intro_music_updated_at !==
            oldRecord.intro_music_updated_at
          ) {
            logger.info('Real-time update: introMusicUpdatedAt changed');
            const newIntroMusicUpdatedAt =
              (newRecord.intro_music_updated_at as string) || null;
            setIntroMusicUpdatedAt(newIntroMusicUpdatedAt);
          }
          if (
            newRecord.outro_music_updated_at !==
            oldRecord.outro_music_updated_at
          ) {
            logger.info('Real-time update: outroMusicUpdatedAt changed');
            const newOutroMusicUpdatedAt =
              (newRecord.outro_music_updated_at as string) || null;
            setOutroMusicUpdatedAt(newOutroMusicUpdatedAt);
          }
          if (
            newRecord.theme_music_updated_at !==
            oldRecord.theme_music_updated_at
          ) {
            logger.info('Real-time update: themeMusicUpdatedAt changed');
            const newThemeMusicUpdatedAt =
              (newRecord.theme_music_updated_at as string) || null;
            setThemeMusicUpdatedAt(newThemeMusicUpdatedAt);
          }
          if (newRecord.announce_updated_at !== oldRecord.announce_updated_at) {
            logger.info('Real-time update: announceUpdatedAt changed');
            const newAnnounceUpdatedAt =
              (newRecord.announce_updated_at as string) || null;
            setAnnounceUpdatedAt(newAnnounceUpdatedAt);
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [record?.id]);

  // Track form changes to detect unsaved changes
  useEffect(() => {
    if (!originalValues) return;

    const hasChanges =
      topic !== originalValues.topic ||
      slug !== originalValues.slug ||
      storyScope !== originalValues.storyScope ||
      titleInstructions !== originalValues.titleInstructions ||
      style !== originalValues.style ||
      audioLogoDesc !== originalValues.audioLogoDesc ||
      musicPrompt !== originalValues.musicPrompt ||
      imagePrompt !== originalValues.imagePrompt ||
      storyScriptPrompt !== originalValues.storyScriptPrompt ||
      enabled !== originalValues.enabled ||
      allowStoryImages !== originalValues.allowStoryImages ||
      genreId !== originalValues.genreId ||
      lang !== originalValues.lang ||
      voiceProfile1 !== originalValues.voiceProfile1 ||
      voiceInstructions1 !== originalValues.voiceInstructions1 ||
      voiceProfile2 !== originalValues.voiceProfile2 ||
      voiceInstructions2 !== originalValues.voiceInstructions2 ||
      voiceIndex1 !== originalValues.voiceIndex1 ||
      voiceIndex2 !== originalValues.voiceIndex2 ||
      hostPersona1 !== originalValues.hostPersona1 ||
      hostPersona2 !== originalValues.hostPersona2 ||
      dynamicsKey !== originalValues.dynamicsKey ||
      scriptInstructions1 !== originalValues.scriptInstructions1 ||
      scriptInstructions2 !== originalValues.scriptInstructions2 ||
      tags !== originalValues.tags ||
      usedDebug !== originalValues.usedDebug ||
      description !== originalValues.description ||
      taxonomy !== originalValues.taxonomy ||
      isDistributed !== originalValues.isDistributed ||
      itunesCategory !== originalValues.itunesCategory ||
      itunesSubcategory !== originalValues.itunesSubcategory ||
      urlYoutube !== originalValues.urlYoutube ||
      urlSpotify !== originalValues.urlSpotify ||
      urlApple !== originalValues.urlApple ||
      mixingSpec !== originalValues.mixingSpec ||
      customPublishWeekdays !== originalValues.customPublishWeekdays;

    setHasUnsavedChanges(hasChanges);
  }, [
    topic,
    slug,
    storyScope,
    titleInstructions,
    style,
    audioLogoDesc,
    musicPrompt,
    imagePrompt,
    storyScriptPrompt,
    enabled,
    allowStoryImages,
    genreId,
    lang,
    voiceProfile1,
    voiceInstructions1,
    voiceProfile2,
    voiceInstructions2,
    voiceIndex1,
    voiceIndex2,
    hostPersona1,
    hostPersona2,
    dynamicsKey,
    scriptInstructions1,
    scriptInstructions2,
    tags,
    usedDebug,
    description,
    taxonomy,
    isDistributed,
    itunesCategory,
    itunesSubcategory,
    urlYoutube,
    urlSpotify,
    urlApple,
    mixingSpec,
    customPublishWeekdays,
    originalValues,
  ]);

  // Handle weekday checkbox toggle
  const handleWeekdayToggle = (day: Weekday) => {
    const newSelected = new Set(selectedWeekdays);
    if (newSelected.has(day)) {
      newSelected.delete(day);
    } else {
      newSelected.add(day);
    }
    setSelectedWeekdays(newSelected);

    // Update comma-separated string
    if (newSelected.size === 0) {
      setCustomPublishWeekdays(null); // Will use default weekday
    } else {
      const sortedDays: Weekday[] = [
        'sun',
        'mon',
        'tue',
        'wed',
        'thu',
        'fri',
        'sat',
      ];
      const orderedDays = sortedDays.filter(d => newSelected.has(d));
      setCustomPublishWeekdays(orderedDays.join(','));
    }
  };

  // Handle browser beforeunload for unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Block navigation (menu clicks, back button) when there are unsaved changes
  // Intercept all link clicks and ask for confirmation if there are unsaved changes
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;

    const handleClick = (e: MouseEvent) => {
      // Check if the click is on a link or inside a link
      const target = e.target as HTMLElement;
      const link = target.closest('a');

      if (link && link.href) {
        // Check if it's an internal navigation link (not external or hash links)
        const url = new URL(link.href);
        const currentUrl = new URL(window.location.href);

        if (
          url.origin === currentUrl.origin &&
          url.pathname !== currentUrl.pathname
        ) {
          // Internal navigation detected
          if (
            !window.confirm(
              'You have unsaved changes. Are you sure you want to leave?'
            )
          ) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }
    };

    // Capture phase to intercept before React Router processes the click
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [hasUnsavedChanges]);

  const validateSlugFormat = (slug: string): boolean => {
    if (!slug || typeof slug !== 'string') return false;
    const slugRegex = /^[a-z0-9-]+$/;
    return slugRegex.test(slug) && !slug.startsWith('-') && !slug.endsWith('-');
  };

  const validateUrl = (
    url: string | null,
    platform: string
  ): string | undefined => {
    // Empty URLs are valid (optional field)
    if (!url || url.trim() === '') return undefined;

    try {
      const parsed = new URL(url);
      // Must be http or https
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        return `Invalid ${platform} URL - must use http or https`;
      }
      return undefined;
    } catch {
      return `Invalid ${platform} URL format`;
    }
  };

  const validateSlug = async (newSlug: string) => {
    if (!newSlug) {
      setSlugError('Slug is required');
      return;
    }

    if (!validateSlugFormat(newSlug)) {
      setSlugError(
        'Slug can only contain lowercase letters, numbers, and hyphens'
      );
      return;
    }

    // Skip uniqueness check if slug matches the original value (user reverted change)
    if (newSlug === record.slug) {
      setSlugError('');
      return;
    }

    const isUnique = await checkSlugUniqueness(newSlug);
    if (!isUnique) {
      setSlugError('This slug is already taken');
      return;
    }

    setSlugError('');
  };

  const handleSlugChange = (newSlug: string) => {
    // Convert to lowercase immediately for display and validation
    const lowercaseSlug = newSlug.toLowerCase();
    setSlug(lowercaseSlug);
    if (lowercaseSlug) {
      validateSlug(lowercaseSlug);
    } else {
      setSlugError('');
    }
  };

  const handleMixingSpecChange = (value: string) => {
    setMixingSpec(value);

    // Trim the value to check for actual content
    const trimmedValue = value.trim();

    // Empty or whitespace-only is valid (will use default spec)
    if (trimmedValue === '') {
      setMixingSpecErrors([]);
      return;
    }

    // If there's any non-whitespace content, validate JSON syntax and schema
    try {
      const parsed = JSON.parse(trimmedValue);
      const errors = validateMixingSpec(parsed);
      setMixingSpecErrors(errors);
    } catch {
      setMixingSpecErrors([{ message: 'Invalid JSON syntax' }]);
    }
  };

  const handleLoadDefaultClick = () => {
    // Block with error dialog if field has content
    if (mixingSpec && mixingSpec.trim().length > 0) {
      setShowMixingSpecBlockedDialog(true);
    } else {
      handleLoadMixingSpec();
    }
  };

  const handleLoadMixingSpec = async () => {
    try {
      // Load default mixing spec from config table
      const { data, error } = await supabase
        .from('config')
        .select('value')
        .eq('key', 'default_mixing_spec')
        .eq('category', 'audio')
        .maybeSingle();

      if (error) {
        logger.error('Failed to load default mixing spec', { error });
        notify('Failed to load default mixing spec', { type: 'error' });
        return;
      }

      if (!data || !data.value) {
        notify('No default mixing spec configured in Settings', {
          type: 'warning',
        });
        return;
      }

      // Format the JSON nicely
      const formatted = formatMixingSpecJson(data.value);
      handleMixingSpecChange(formatted);
      notify('Loaded default mixing spec', { type: 'info' });
    } catch (err) {
      logger.error('Error loading default mixing spec', { error: err });
      notify('Error loading default mixing spec', { type: 'error' });
    }
  };

  const handleLoadScriptPromptClick = () => {
    // Block with error dialog if field has content
    if (storyScriptPrompt && storyScriptPrompt.trim().length > 0) {
      setShowScriptPromptBlockedDialog(true);
    } else {
      handleLoadScriptPrompt();
    }
  };

  const handleLoadScriptPrompt = async () => {
    try {
      const promptContent = await getGenPromptContent('story_script');

      if (!promptContent) {
        notify('No default script prompt configured', {
          type: 'warning',
        });
        return;
      }

      setStoryScriptPrompt(promptContent);
      notify('Loaded default script prompt', { type: 'info' });
    } catch (err) {
      logger.error('Error loading default script prompt', { error: err });
      notify('Error loading default script prompt', { type: 'error' });
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
      if (!confirmed) return;
    }
    redirect('list', 'shows');
  };

  const handleSave = async () => {
    if (!slug || !style || !topic || slugError || !record) return;
    if (mixingSpecErrors.length > 0) {
      notify('Please fix mixing spec validation errors before saving', {
        type: 'error',
      });
      return;
    }

    setIsSaving(true);

    // Exclude computed/joined fields from update data
    const { genre: _genre, ...recordWithoutJoins } = record;

    // Parse mixing spec JSON string to array (or null if empty)
    let parsedMixingSpec: MixingTrack[] | null = null;
    if (mixingSpec.trim()) {
      try {
        parsedMixingSpec = JSON.parse(mixingSpec);
      } catch {
        notify('Invalid mixing spec JSON', { type: 'error' });
        setIsSaving(false);
        return;
      }
    }

    const updatedShow: Show = {
      ...recordWithoutJoins,
      slug,
      topic,
      storyScope: storyScope || undefined,
      titleInstructions: titleInstructions || null,
      style,
      voiceProfile1: voiceProfile1 || undefined,
      // Force voice instructions to null if persona selected, otherwise use custom instructions
      voiceInstructions1: hostPersona1 ? null : voiceInstructions1 || null,
      voiceProfile2: voiceProfile2 || undefined,
      voiceInstructions2: hostPersona2 ? null : voiceInstructions2 || null,
      voiceIndex1: voiceIndex1 ?? undefined,
      voiceIndex2: voiceIndex2 ?? undefined,
      hostPersona1: hostPersona1 || null,
      hostPersona2: hostPersona2 || null,
      // Force script instructions to null if persona selected, otherwise use custom instructions
      scriptInstructions1: hostPersona1 ? null : scriptInstructions1 || null,
      scriptInstructions2: hostPersona2 ? null : scriptInstructions2 || null,
      dynamicsKey: dynamicsKey || null,
      genreId: genreId || undefined,
      lang, // Language: null = all languages
      musicPrompt: musicPrompt || undefined,
      imagePrompt: imagePrompt || undefined,
      imageUpdatedAt,
      musicUpdatedAt,
      introMusicUpdatedAt,
      outroMusicUpdatedAt,
      themeMusicUpdatedAt,
      // Note: announceUpdatedAt belongs to show_translations, not shows
      tags: tags || undefined,
      enabled,
      isDistributed,
      usedDebug,
      taxonomy: taxonomy || null,
      itunesCategory: itunesCategory || null,
      itunesSubcategory: itunesSubcategory || null,
      urlYoutube: urlYoutube?.trim() ? urlYoutube : null,
      urlSpotify: urlSpotify?.trim() ? urlSpotify : null,
      urlApple: urlApple?.trim() ? urlApple : null,
      mixingSpec: parsedMixingSpec,
      customPublishWeekdays: customPublishWeekdays || null,
    };

    try {
      logger.info('DEBUG ShowEdit: saving show with lang', {
        lang,
        updatedShowLang: updatedShow.lang,
      });
      await update('shows', {
        id: record.id,
        data: updatedShow,
        previousData: record,
      });

      // Update originalValues with current state to reset change detection
      setOriginalValues({
        topic,
        slug: slug || '',
        storyScope: storyScope || '',
        titleInstructions: titleInstructions || '',
        style: style || '',
        audioLogoDesc: audioLogoDesc || '',
        musicPrompt: musicPrompt || '',
        imagePrompt: imagePrompt || '',
        storyScriptPrompt: storyScriptPrompt || '',
        enabled,
        allowStoryImages,
        voiceProfile1: voiceProfile1 || '',
        voiceInstructions1: hostPersona1 ? '' : voiceInstructions1 || '',
        voiceProfile2: voiceProfile2 || '',
        voiceInstructions2: hostPersona2 ? '' : voiceInstructions2 || '',
        voiceIndex1: voiceIndex1 ?? null,
        voiceIndex2: voiceIndex2 ?? null,
        hostPersona1: hostPersona1 || null,
        hostPersona2: hostPersona2 || null,
        scriptInstructions1: hostPersona1 ? '' : scriptInstructions1 || '',
        scriptInstructions2: hostPersona2 ? '' : scriptInstructions2 || '',
        dynamicsKey: dynamicsKey || null,
        genreId: genreId || '',
        lang,
        tags: tags || '',
        usedDebug,
        description: description || '',
        taxonomy: taxonomy || '',
        isDistributed,
        itunesCategory: itunesCategory || '',
        itunesSubcategory: itunesSubcategory || '',
        urlYoutube: urlYoutube || '',
        urlSpotify: urlSpotify || '',
        urlApple: urlApple || '',
        mixingSpec,
        customPublishWeekdays: customPublishWeekdays || null,
      });

      setHasUnsavedChanges(false); // Clear unsaved changes flag
      notify('Show updated successfully', {
        type: 'success',
        action: (
          <Button
            size="small"
            onClick={() => redirect('list', 'shows')}
            sx={{
              color: 'inherit',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Done
          </Button>
        ),
      });
      // Stay on the page for "save in place" functionality
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('Failed to update show:', error);
      notify(`Error updating show: ${errorMessage}`, { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (!record) return;

    // Check if we need protected confirmation
    const needsProtection = isProduction() && enabled;

    if (needsProtection) {
      setShowProtectedDeleteDialog(true);
    } else {
      setShowStandardDeleteDialog(true);
    }
  };

  const performDelete = async () => {
    if (!record) return;

    try {
      // Delete show from database
      await deleteOne('shows', { id: record.id, previousData: record });

      // Delete associated storage folder using slug
      if (record.slug) {
        try {
          const { deleteEntityStorage } = await import(
            '../../../services/storageCleanupApi'
          );
          const storageResult = await deleteEntityStorage('show', record.slug);
          if (!storageResult.success) {
            logger.warn(
              'Storage cleanup failed for show:',
              storageResult.error
            );
            // Continue with success notification, storage cleanup is not critical
          }
        } catch (storageError) {
          logger.warn('Storage cleanup error:', storageError);
          // Continue with success notification, storage cleanup is not critical
        }
      }

      setHasUnsavedChanges(false); // Clear unsaved changes flag
      setShowProtectedDeleteDialog(false);
      setShowStandardDeleteDialog(false);
      notify('Show deleted successfully');
      redirect('list', 'shows');
    } catch (error) {
      logger.error('Failed to delete show:', error);
      notify('Error deleting show', { type: 'error' });
    }
  };

  const handleCloseDeleteDialogs = () => {
    setShowProtectedDeleteDialog(false);
    setShowStandardDeleteDialog(false);
  };

  const handleGenerateImage = async () => {
    if (!record?.id || !imagePrompt) {
      notify('Missing required fields: showId or imagePrompt', {
        type: 'error',
      });
      return;
    }

    try {
      // Start the generation job (returns immediately with jobId)
      const result = await generateShowImage({
        showId: record.id,
        showSlug: slug || '',
        showTopic: topic,
        imagePrompt,
      });

      if (result.success && result.jobId) {
        // Watch for job completion via real-time subscription
        watchJob(result.jobId);
      } else {
        notify(result.error || 'Failed to start image generation', {
          type: 'error',
        });
      }
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Unknown error occurred', {
        type: 'error',
      });
    }
  };

  // Theme music generation handler (Issue #601)
  const handleGenerateThemeMusic = async () => {
    if (!record?.id || !musicPrompt) {
      notify('Missing required fields: showId or musicPrompt', {
        type: 'error',
      });
      return;
    }

    try {
      // Start the generation job (returns immediately with jobId)
      const result = await generateShowThemeMusic({
        showSlug: slug || '',
      });

      if (result.success && result.jobId) {
        // Watch for job completion via real-time subscription
        watchMusicJob(result.jobId);
      } else {
        notify(result.error || 'Failed to start theme music generation', {
          type: 'error',
        });
      }
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Unknown error occurred', {
        type: 'error',
      });
    }
  };

  if (!record) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
        width: '100%',
        maxWidth: LAYOUT.mediumContentWidth,
        mx: 'auto',
        pb: 12,
      }}
    >
      {/* Custom header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <IconButton onClick={handleCancel} sx={{ p: 1 }}>
          <ArrowLeft size={20} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5" component="h1">
            {record?.slug || 'Edit Show'}
          </Typography>
          {!enabled && <Chip label="Disabled" color="warning" size="small" />}
          <DebugBadge show={usedDebug} size={20} />
        </Box>
      </Box>
      <EntityMetadata
        id={record.id}
        presets={[{ label: 'Profile', presetKey: record.profilePresetUsed }]}
      >
        {storyCount > 0 && (
          <Button
            variant="text"
            size="small"
            onClick={e => {
              e.stopPropagation();
              navigate(
                `/stories?filter=${encodeURIComponent(JSON.stringify({ showSlug: record.slug }))}`
              );
            }}
            sx={{
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
              padding: '2px 8px',
              minWidth: 'auto',
              textTransform: 'none',
              fontSize: '0.75rem',
              color: 'primary.main',
              whiteSpace: 'nowrap',
            }}
          >
            {storyCount === 1 ? '1 story' : `${storyCount} stories`}
          </Button>
        )}
      </EntityMetadata>

      {/* Show Actions Banner - Missing Details */}
      <ShowActionsSection
        customPublishWeekdays={customPublishWeekdays}
        slug={slug}
        mixingSpec={mixingSpec}
        imageUpdatedAt={imageUpdatedAt}
        introMusicUpdatedAt={introMusicUpdatedAt}
        outroMusicUpdatedAt={outroMusicUpdatedAt}
      />

      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, newValue) => setActiveTab(newValue)}
        >
          <Tab label="Details" />
          <Tab label="Speakers" />
          {/* <Tab label="Script Prompt" /> */}
          <Tab label="Image" />
          <Tab label="Audio" />
          <Tab label="Distribution" />
          <Tab label="Languages" />
        </Tabs>
      </Box>

      {/* Tab 0: Details */}
      {activeTab === 0 && (
        <Box>
          {/* Issue #767: Show-level Enabled toggle as a full-width row */}
          <EntityEnabledToggle
            enabled={enabled}
            disabledReason={showDisabledReason}
            disabledAt={showDisabledAt}
            busy={enabledBusy}
            sx={{ mb: 2 }}
            onToggle={async (next, reason) => {
                setEnabledBusy(true);
                const disabledAt = new Date().toISOString();
                const disabledReason = reason || 'Manually disabled';
                try {
                  if (next) {
                    const { data, error } = await supabase.functions.invoke(
                      'reenable-entity',
                      { body: { entityType: 'show', entityId: record.id } }
                    );
                    if (error || !data?.success) {
                      throw new Error(
                        error?.message || data?.error || 'Re-enable failed'
                      );
                    }
                  } else {
                    const { error } = await supabase
                      .from('shows')
                      .update({
                        enabled: false,
                        disabled_at: disabledAt,
                        disabled_reason: disabledReason,
                      })
                      .eq('id', record.id);
                    if (error) throw new Error(error.message);
                  }
                  setEnabled(next);
                  setShowDisabledReason(next ? null : disabledReason);
                  setShowDisabledAt(next ? null : disabledAt);
                  notify(next ? 'Show enabled' : 'Show disabled', {
                    type: 'success',
                  });
                  refresh();
                } catch (err) {
                  notify(
                    err instanceof Error ? err.message : 'Update failed',
                    { type: 'error' }
                  );
                } finally {
                  setEnabledBusy(false);
                }
              }}
            />

          {/* Slug field */}
          <FormTextField
            label="Slug"
            value={slug}
            onChange={e => handleSlugChange(e.target.value)}
            placeholder="e.g., my-podcast-show"
            helperText={slugError || ''}
            error={!!slugError}
            required
          />

          <FormTextField
            label="Taxonomy"
            value={taxonomy}
            onChange={e => setTaxonomy(e.target.value)}
            placeholder="Internal categorization terms (not for search)"
          />

          <GenreSelector
            selectedGenreId={genreId}
            onGenreChange={setGenreId}
            width="medium"
          />

          <FormTextField
            label="What is the show about?"
            value={topic}
            onChange={e => setTopic(e.target.value)}
            placeholder="e.g., Emotional stories from history"
            width="full"
            required
          />

          <FormTextField
            label="Story Scope"
            value={storyScope}
            onChange={e => setStoryScope(e.target.value)}
            placeholder="e.g., Stories about historical events with surprising facts. Must be true stories."
            rows={3}
          />

          <FormTextField
            label="Title Instructions"
            value={titleInstructions}
            onChange={e => setTitleInstructions(e.target.value)}
            placeholder="e.g., Name the object/person in the title, or Use the date in YYYY-MM-DD format"
            rows={1}
          />

          <TagsInput value={tags} onChange={setTags} label="Tags" fullWidth />

          <FormTextField
            label="Style Description"
            value={style}
            onChange={e => setStyle(e.target.value)}
            rows={15}
            required
          />

          {/* Debug Mode Flag */}
          <FormControlLabel
            control={
              <Checkbox
                checked={usedDebug}
                onChange={e => setUsedDebug(e.target.checked)}
                sx={{
                  color: usedDebug ? '#f44336' : undefined,
                  '&.Mui-checked': {
                    color: '#f44336',
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{ color: usedDebug ? '#f44336' : 'text.secondary' }}
                >
                  Generated with debug model preset
                </Typography>
                <DebugBadge show={usedDebug} size={14} showTooltip={false} />
              </Box>
            }
          />
        </Box>
      )}

      {/* Tab 1: Speakers */}
      {activeTab === 1 && (
        <Box>
          <ShowEditHostsTab
            dynamicsKey={dynamicsKey}
            onDynamicsKeyChange={setDynamicsKey}
            hostPersona1={hostPersona1}
            onHostPersona1Change={handlePersona1Change}
            voiceProfile1={voiceProfile1}
            onVoiceProfile1Change={setVoice1Profile}
            voiceIndex1={voiceIndex1}
            onVoiceIndex1Change={setVoiceIndex1}
            voiceInstructions1={voiceInstructions1}
            onVoiceInstructions1Change={setVoice1Instructions}
            scriptInstructions1={scriptInstructions1}
            onScriptInstructions1Change={setScriptInstructions1}
            hostPersona2={hostPersona2}
            onHostPersona2Change={handlePersona2Change}
            voiceProfile2={voiceProfile2}
            onVoiceProfile2Change={setVoice2Profile}
            voiceIndex2={voiceIndex2}
            onVoiceIndex2Change={setVoiceIndex2}
            voiceInstructions2={voiceInstructions2}
            onVoiceInstructions2Change={setVoice2Instructions}
            scriptInstructions2={scriptInstructions2}
            onScriptInstructions2Change={setScriptInstructions2}
            personas={personas}
          />
        </Box>
      )}

      {/* Tab 2: Script Prompt */}
      {/* {activeTab === 2 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Available placeholders: {'{show-name}'}, {'{show-style}'},{' '}
            {'{show-topic}'}, {'{story-brief}'}, {'{story-takeaway}'}
          </Typography>

          <TextField
            label="Custom Script Prompt (Optional)"
            value={storyScriptPrompt || ''}
            onChange={e => setStoryScriptPrompt(e.target.value)}
            multiline
            minRows={30}
            maxRows={80}
            fullWidth
            helperText="Leave empty to use the global story_script prompt template"
            InputProps={{
              style: { fontFamily: 'monospace', fontSize: '14px' },
            }}
            sx={{
              '& textarea': {
                resize: 'vertical',
              },
            }}
          />

          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              onClick={handleLoadScriptPromptClick}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                p: 0,
                minWidth: 'auto',
                textDecoration: 'underline',
              }}
            >
              Load default
            </Button>
          </Box>
        </Box>
      )} */}

      {/* Tab 2: Image */}
      {activeTab === 2 && (
        <Box>
          <ImageGenerationSection
            entityType="show"
            entityId={record?.id}
            entitySlug={slug || ''}
            entityName={slug || ''}
            showTopic={topic}
            languageKey={lang}
            imagePrompt={imagePrompt}
            imageUpdatedAt={imageUpdatedAt}
            onImagePromptChange={value => setImagePrompt(value)}
            onImageUpdatedAtChange={setImageUpdatedAt}
            onGenerate={handleGenerateImage}
            isGenerating={isGeneratingImage}
            generationError={null}
          />

          {/* Allow Story Images Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={allowStoryImages}
                onChange={e => setAllowStoryImages(e.target.checked)}
              />
            }
            label="Allow individual story images"
            sx={{ mt: 2 }}
          />
        </Box>
      )}

      {/* Tab 3: Audio */}
      {activeTab === 3 && (
        <Box>
          {/* Audio Section */}
          <Box>
            <MusicUploadSection
              showSlug={slug}
              showId={record?.id}
              musicPrompt={musicPrompt}
              onMusicPromptChange={newMusicPrompt =>
                setMusicPrompt(newMusicPrompt)
              }
              musicUpdatedAt={musicUpdatedAt}
              onMusicDeleted={() => {
                setMusicUpdatedAt(null);
                setHasUnsavedChanges(true);
              }}
              onMusicUpdatedAtChanged={timestamp => {
                setMusicUpdatedAt(timestamp);
              }}
            />

            <Box sx={{ mb: 2 }} />

            <ThemeMusicSection
              showSlug={slug || ''}
              showId={record?.id}
              themeMusicUpdatedAt={themeMusicUpdatedAt}
              musicPrompt={musicPrompt}
              onThemeMusicDeleted={() => {
                setThemeMusicUpdatedAt(null);
                setHasUnsavedChanges(true);
              }}
              onThemeMusicUpdatedAtChanged={timestamp => {
                setThemeMusicUpdatedAt(timestamp);
              }}
              onGenerate={handleGenerateThemeMusic}
              isGenerating={isGeneratingThemeMusic}
            />

            <Box sx={{ mb: 2 }} />

            <IntroMusicSection
              showSlug={slug || ''}
              showId={record?.id}
              introMusicUpdatedAt={introMusicUpdatedAt}
              onIntroMusicDeleted={() => {
                setIntroMusicUpdatedAt(null);
                setHasUnsavedChanges(true);
              }}
              onIntroMusicUpdatedAtChanged={timestamp => {
                setIntroMusicUpdatedAt(timestamp);
              }}
            />

            <Box sx={{ mb: 2 }} />

            <OutroMusicSection
              showSlug={slug || ''}
              showId={record?.id}
              outroMusicUpdatedAt={outroMusicUpdatedAt}
              onOutroMusicDeleted={() => {
                setOutroMusicUpdatedAt(null);
                setHasUnsavedChanges(true);
              }}
              onOutroMusicUpdatedAtChanged={timestamp => {
                setOutroMusicUpdatedAt(timestamp);
              }}
            />

            {/* Custom Mixing Spec */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Custom Mixing Spec (Optional)
              </Typography>

              {mixingSpecErrors.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">
                    Validation Errors:
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {mixingSpecErrors.map(err => (
                      <li key={`${err.path || 'root'}-${err.message}`}>
                        {err.path ? <code>{err.path}</code> : null}{' '}
                        {err.message}
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}

              <TextField
                multiline
                fullWidth
                minRows={6}
                maxRows={15}
                value={mixingSpec}
                onChange={e => handleMixingSpecChange(e.target.value)}
                placeholder="Leave empty to use default, or enter JSON array of tracks..."
                sx={{
                  fontFamily: 'monospace',
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  },
                }}
                error={mixingSpecErrors.length > 0}
              />

              {/* Help Panel Toggle and Load Default Links */}
              <Box sx={{ mt: 1, mb: 1, display: 'flex', gap: 2 }}>
                <Button
                  size="small"
                  onClick={() => setShowMixingHelp(!showMixingHelp)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    p: 0,
                    minWidth: 'auto',
                    textDecoration: 'underline',
                  }}
                  startIcon={
                    showMixingHelp ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )
                  }
                >
                  {showMixingHelp ? 'Hide Help' : 'Show Help'}
                </Button>
                <Button
                  size="small"
                  onClick={handleLoadDefaultClick}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    p: 0,
                    minWidth: 'auto',
                    textDecoration: 'underline',
                  }}
                >
                  Load default
                </Button>
              </Box>

              {/* Collapsible Help Panel */}
              <Collapse in={showMixingHelp}>
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Track Properties:
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>
                        <strong>id</strong>: Track identifier (required if
                        referenced by another track)
                      </li>
                      <li>
                        <strong>source</strong>: Audio URL or placeholder
                        (required)
                      </li>
                      <li>
                        <strong>clipStart/clipEnd</strong>: Trim range in
                        seconds (negative = from end)
                      </li>
                      <li>
                        <strong>offset</strong>: When to start - number or
                        &quot;trackId anchor shift&quot;
                      </li>
                      <li>
                        <strong>keyframes</strong>: Volume automation [time,
                        volume, time, volume, ...]
                      </li>
                    </ul>
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom>
                    Placeholders:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {'{{voice-path}}'} = Story voice audio (required)
                    <br />
                    {'{{intro-music-path}}'} = Show intro music (required)
                    <br />
                    {'{{outro-music-path}}'} = Show outro music (optional, track
                    removed if missing)
                    <br />
                    {'{{music-path}}'} = Background music (optional, track
                    removed if missing)
                    <br />
                    {'{{signature-path}}'} = Signature audio from config
                    (optional, track removed if not configured)
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom>
                    Offset Examples:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <code>0</code> - Start at beginning
                    <br />
                    <code>&quot;intro start 8.5&quot;</code> - Start 8.5s after
                    intro starts
                    <br />
                    <code>&quot;intro end -1.5&quot;</code> - Start 1.5s before
                    intro ends
                  </Typography>
                </Paper>
              </Collapse>

              <AudioMixTester
                showSlug={slug || ''}
                showId={record.id}
                showLang={lang}
                introMusicUpdatedAt={introMusicUpdatedAt}
                outroMusicUpdatedAt={outroMusicUpdatedAt}
                musicUpdatedAt={musicUpdatedAt}
                themeMusicUpdatedAt={themeMusicUpdatedAt}
                announceUpdatedAt={announceUpdatedAt}
                mixingSpec={(() => {
                  // Trim the spec first to catch whitespace-only values
                  const trimmedSpec = mixingSpec?.trim() || '';
                  // Only use custom spec if it has at least one non-whitespace character
                  if (trimmedSpec.length === 0) return null;
                  try {
                    return JSON.parse(trimmedSpec);
                  } catch {
                    return null;
                  }
                })()}
                disabled={
                  mixingSpecErrors.length > 0 && mixingSpec?.trim().length > 0
                }
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* Tab 4: Distribution */}
      {activeTab === 4 && (
        <Box>
          {/* iTunes Categories */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              mt: 2,
            }}
          >
            <FormTextField
              label="iTunes Category"
              value={itunesCategory}
              onChange={e => setItunesCategory(e.target.value)}
              placeholder="e.g., True Crime, History"
            />
            <FormTextField
              label="iTunes Subcategory"
              value={itunesSubcategory}
              onChange={e => setItunesSubcategory(e.target.value)}
              placeholder="e.g., Documentary (optional)"
            />
          </Box>

          {/* Publishing Schedule */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Publishing Schedule
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[
                { value: 'mon' as Weekday, label: 'Mon' },
                { value: 'tue' as Weekday, label: 'Tue' },
                { value: 'wed' as Weekday, label: 'Wed' },
                { value: 'thu' as Weekday, label: 'Thu' },
                { value: 'fri' as Weekday, label: 'Fri' },
                { value: 'sat' as Weekday, label: 'Sat' },
                { value: 'sun' as Weekday, label: 'Sun' },
              ].map(({ value, label }) => (
                <FormControlLabel
                  key={value}
                  control={
                    <Checkbox
                      checked={selectedWeekdays.has(value)}
                      onChange={() => handleWeekdayToggle(value)}
                    />
                  }
                  label={label}
                  sx={{ mr: 1 }}
                />
              ))}
            </Box>
          </Box>

          {/* Issue #667: External Distribution Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={isDistributed}
                onChange={e => {
                  if (!e.target.checked && isDistributed) {
                    // Turning OFF — show destructive confirmation
                    setShowDistributionOffDialog(true);
                  } else {
                    setIsDistributed(e.target.checked);
                  }
                }}
              />
            }
            label="Distribute externally"
            sx={{ mt: 3 }}
          />

          {/* Distribution Platform URLs - only shown when distributed */}
          {isDistributed && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <FormTextField
                  label="YouTube URL"
                  value={urlYoutube}
                  onChange={e => {
                    const newValue = e.target.value;
                    setUrlYoutube(newValue);
                    const error = validateUrl(newValue, 'YouTube');
                    setUrlErrors(prev => ({ ...prev, youtube: error }));
                  }}
                  placeholder="https://youtube.com/@yourshow"
                  helperText={urlErrors.youtube}
                  error={!!urlErrors.youtube}
                  width="full"
                />
                {urlYoutube && !urlErrors.youtube && (
                  <IconButton
                    size="small"
                    href={urlYoutube}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mt: 2 }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <FormTextField
                  label="Spotify URL"
                  value={urlSpotify}
                  onChange={e => {
                    const newValue = e.target.value;
                    setUrlSpotify(newValue);
                    const error = validateUrl(newValue, 'Spotify');
                    setUrlErrors(prev => ({ ...prev, spotify: error }));
                  }}
                  placeholder="https://open.spotify.com/show/..."
                  helperText={urlErrors.spotify}
                  error={!!urlErrors.spotify}
                  width="full"
                />
                {urlSpotify && !urlErrors.spotify && (
                  <IconButton
                    size="small"
                    href={urlSpotify}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mt: 2 }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <FormTextField
                  label="Apple Podcasts URL"
                  value={urlApple}
                  onChange={e => {
                    const newValue = e.target.value;
                    setUrlApple(newValue);
                    const error = validateUrl(newValue, 'Apple Podcasts');
                    setUrlErrors(prev => ({ ...prev, apple: error }));
                  }}
                  placeholder="https://podcasts.apple.com/..."
                  helperText={urlErrors.apple}
                  error={!!urlErrors.apple}
                  width="full"
                />
                {urlApple && !urlErrors.apple && (
                  <IconButton
                    size="small"
                    href={urlApple}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mt: 2 }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {/* RSS Feed Link */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <FormTextField
                  label="RSS Feed"
                  value={slug ? `${ENV_CONFIG.API_BASE_URL}/shows/${slug}/rss` : ''}
                  onChange={() => {}} // Read-only field
                  placeholder="RSS feed URL (auto-generated)"
                  width="full"
                  disabled
                />
                {slug && (
                  <IconButton
                    size="small"
                    href={`${ENV_CONFIG.API_BASE_URL}/shows/${slug}/rss`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mt: 2 }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {/* API Endpoint Link */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <FormTextField
                  label="API"
                  value={slug ? `${ENV_CONFIG.API_BASE_URL}/shows/${slug}` : ''}
                  onChange={() => {}} // Read-only field
                  placeholder="API endpoint URL (auto-generated)"
                  width="full"
                  disabled
                />
                {slug && (
                  <IconButton
                    size="small"
                    href={`${ENV_CONFIG.API_BASE_URL}/shows/${slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mt: 2 }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Tab 5: Languages */}
      {activeTab === 5 && (
        <ShowEditLanguagesTab
          showId={record.id}
          showSlug={slug || ''}
          showLang={lang}
          onLanguageChange={setLang}
        />
      )}

      {/* Save/Cancel Buttons - Outside tabs, always visible */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          py: 2,
          px: 3,
          zIndex: 1000,
        }}
      >
        {/* Back button on the left */}
        <IconButton onClick={handleCancel} sx={{ p: 1 }} title="Go back">
          <ArrowLeft size={20} />
        </IconButton>

        {/* Unsaved changes indicator in the center */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          {hasUnsavedChanges && !isSaving && (
            <Chip
              label="Unsaved changes"
              sx={{
                bgcolor: '#ed6c02',
                color: 'white',
                fontWeight: 500,
                fontSize: '0.875rem',
              }}
            />
          )}
        </Box>

        {/* Action buttons on the right */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              !slug ||
              !style ||
              !topic ||
              !!slugError ||
              !!urlErrors.youtube ||
              !!urlErrors.spotify ||
              !!urlErrors.apple ||
              isSaving
            }
            sx={{ minWidth: 120 }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {/* Error Dialog for Mixing Spec Load Default Blocked */}
      <Dialog
        open={showMixingSpecBlockedDialog}
        onClose={() => setShowMixingSpecBlockedDialog(false)}
      >
        <DialogTitle>Cannot Load Default</DialogTitle>
        <DialogContent>
          <Typography>
            Please clear the field manually before loading the default template.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowMixingSpecBlockedDialog(false)}
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog for Script Prompt Load Default Blocked */}
      <Dialog
        open={showScriptPromptBlockedDialog}
        onClose={() => setShowScriptPromptBlockedDialog(false)}
      >
        <DialogTitle>Cannot Load Default</DialogTitle>
        <DialogContent>
          <Typography>
            Please clear the field manually before loading the default template.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowScriptPromptBlockedDialog(false)}
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Protected Delete Dialog for Production Enabled Shows */}
      <ProtectedDeleteDialog
        open={showProtectedDeleteDialog}
        onClose={handleCloseDeleteDialogs}
        onConfirm={performDelete}
        title="Delete Enabled Show"
        message={`You are about to delete "${record.slug}" in production. This show is currently enabled and will be removed from the public site.${
          hasUnsavedChanges
            ? ' Your unsaved changes will also be discarded.'
            : ''
        }`}
      />

      {/* Standard Delete Dialog */}
      <Dialog
        open={showStandardDeleteDialog}
        onClose={handleCloseDeleteDialogs}
      >
        <DialogTitle>Delete Show</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{record.slug}&quot;?
            {hasUnsavedChanges &&
              ' This will also discard your unsaved changes.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialogs} color="inherit">
            Cancel
          </Button>
          <Button onClick={performDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Issue #667: Destructive confirmation when turning off distribution */}
      <Dialog
        open={showDistributionOffDialog}
        onClose={() => setShowDistributionOffDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderTop: '3px solid',
            borderColor: 'error.main',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AlertTriangle size={24} color="#f44336" />
            <Typography variant="h6" component="span">
              Disable External Distribution
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            This will <strong>delete all external audio files</strong> for every story in this show
            and clear their external audio metadata.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Stories will remain published internally. External audio can be regenerated
            later by re-enabling distribution.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDistributionOffDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setIsDistributed(false);
              setShowDistributionOffDialog(false);
            }}
            color="error"
            variant="contained"
          >
            Disable Distribution
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const ShowEdit = () => {
  const { id } = useParams<{ id: string }>();

  const showId = id || '';

  // Use useGetOne to fetch the record by ID for editing
  const {
    data: record,
    isLoading,
    error,
  } = useGetOne<ShowRecord>(
    'shows',
    { id: showId },
    {
      enabled: !!id && typeof id === 'string',
    }
  );

  // Ensure we have an ID before proceeding
  if (!id || typeof id !== 'string') {
    return <div>Invalid show URL</div>;
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error || !record) {
    return <div>Show not found</div>;
  }

  // Return ShowEditForm directly without white container wrapper
  return <ShowEditForm record={record} />;
};
